/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { TIPOS_ACTIVIDAD, TIPOS_ACTIVIDAD_AGENDABLES } from "../../../constants/tiposActividad";
import styles from "./AgendarMovimientoModal.module.scss";

// Fecha por defecto: próximo día hábil a las 10:00 (minimiza clics; el usuario
// ajusta). Salta sábado/domingo para caer siempre en un día seleccionable
// (filterDate deshabilita el fin de semana → evita abrir en un día bloqueado).
const getDefaultFecha = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  while (d.getDay() === 0 || d.getDay() === 6) {
    d.setDate(d.getDate() + 1);
  }
  return d;
};

const todayStart = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Modal para agendar / editar un "movimiento futuro" en Google Calendar.
 * Controlado por el padre. Dos modos según `evento`:
 *   - crear: evento = null
 *   - editar: evento = { id, start, description, private:{tipo,...} } → precarga
 * Props: isOpen, onClose, onSave({tipo,tipo_label,fecha_inicio,nota}), onDelete,
 *        saving, deleting, clienteNombre, lado, evento.
 */
export const AgendarMovimientoModal = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  saving = false,
  deleting = false,
  clienteNombre = "",
  lado = "",
  evento = null,
}) => {
  const [tipo, setTipo] = useState("");
  const [fecha, setFecha] = useState(getDefaultFecha());
  const [nota, setNota] = useState("");
  const [showNota, setShowNota] = useState(false);

  const isEdit = !!evento;
  const busy = saving || deleting;

  // Precarga al abrir: desde el evento (editar) o defaults (crear).
  useEffect(() => {
    if (!isOpen) return;
    if (evento) {
      const start = evento.start?.dateTime || evento.start?.date;
      const desc = evento.description || "";
      // Si el evento ya venció (start < ahora), NO precargar esa fecha pasada:
      // queda bloqueada por minDate y abriría el calendario en un mes todo gris.
      // Se arranca en el default futuro; tipo y nota sí se conservan (reagendar).
      const vencido = start ? new Date(start).getTime() < Date.now() : false;
      setTipo(evento.private?.tipo || "");
      setFecha(start && !vencido ? new Date(start) : getDefaultFecha());
      setNota(desc);
      setShowNota(!!desc);
    } else {
      setTipo("");
      setFecha(getDefaultFecha());
      setNota("");
      setShowNota(false);
    }
  }, [isOpen, evento]);

  const handleGuardar = () => {
    if (!tipo || busy) return;
    const cfg = TIPOS_ACTIVIDAD[tipo] || {};
    onSave({
      tipo,
      // tipo_label: el label VISIBLE del botón (labelAgenda || label) → es la
      // "acción" que aparece en el título del evento "[cliente] | [acción]".
      tipo_label: cfg.labelAgenda || cfg.label || tipo,
      fecha_inicio: fecha.toISOString(),
      nota: nota.trim(),
    });
  };

  // Acota la lista de horas a 09:00–17:30 (inclusive) y, además, bloquea las horas
  // ya pasadas cuando el día elegido es hoy (en días futuros todas quedan disponibles).
  const filterTime = (time) => {
    const t = new Date(time);
    const mins = t.getHours() * 60 + t.getMinutes();
    const dentroHorario = mins >= 9 * 60 && mins <= 17 * 60 + 30; // 09:00–17:30
    const noPasada = new Date().getTime() < t.getTime();
    return dentroHorario && noPasada;
  };

  // Solo días hábiles (Lu–Vi): deshabilita sábado (6) y domingo (0) en la grilla.
  const esDiaHabil = (date) => {
    const d = date.getDay();
    return d !== 0 && d !== 6;
  };

  return (
    <Modal isOpen={isOpen} toggle={busy ? undefined : onClose} centered className={styles.dialog}>
      <ModalHeader toggle={busy ? undefined : onClose}>
        {isEdit ? "Editar agenda futura" : "Agenda futura"}
        {clienteNombre ? ` | ${clienteNombre}` : ""}
        {lado ? <span style={{ fontSize: "0.8rem", color: "#666", marginLeft: 6 }}>({lado})</span> : null}
      </ModalHeader>
      <ModalBody>
        {/* Row de 3 columnas de alto idéntico: botones | calendario | horas */}
        <div className={styles.row}>
          {/* Columna 1: tipos (vertical, se estiran a llenar el alto) */}
          <div className={styles.leftCol}>
            {TIPOS_ACTIVIDAD_AGENDABLES.map((t) => {
              const cfg = TIPOS_ACTIVIDAD[t] || {};
              const active = tipo === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTipo(t)}
                  disabled={busy}
                  className={styles.typeBtn}
                  style={
                    active
                      ? { backgroundColor: cfg.bg, borderColor: cfg.bg, color: "#fff", fontWeight: 700 }
                      : undefined
                  }
                >
                  {cfg.labelAgenda || cfg.label || t}
                </button>
              );
            })}
          </div>

          {/* Columnas 2 + 3: calendario (define el alto) + lista de horas */}
          <div className={styles.calendarWrap}>
            <DatePicker
              selected={fecha}
              onChange={(d) => d && setFecha(d)}
              showTimeSelect
              timeIntervals={30}
              minDate={todayStart()}
              filterDate={esDiaHabil}
              filterTime={filterTime}
              dateFormat="dd/MM/yyyy HH:mm"
              timeFormat="HH:mm"
              disabled={busy}
              inline
            />
          </div>
        </div>

        {/* Fila inferior (cruza las 3 columnas): nota desplegable */}
        <div className={styles.notaRow}>
          {!showNota ? (
            <button type="button" className={styles.addNoteBtn} onClick={() => setShowNota(true)} disabled={busy}>
              + Agregar nota
            </button>
          ) : (
            <textarea
              className={styles.nota}
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              disabled={busy}
              rows={3}
              maxLength={500}
              placeholder="Detalle breve…"
              autoFocus
            />
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        {isEdit && (
          <Button color="danger" size="sm" outline onClick={onDelete} disabled={busy} style={{ marginRight: "auto" }}>
            {deleting ? "Borrando…" : "Borrar"}
          </Button>
        )}
        <Button color="secondary" size="sm" onClick={onClose} disabled={busy}>
          Cancelar
        </Button>
        <Button color="primary" size="sm" onClick={handleGuardar} disabled={!tipo || busy}>
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AgendarMovimientoModal;
